// Reads the natural pixel dimensions of a media file (image or video) via a
// transient Object URL + a hidden HTMLImageElement / HTMLVideoElement. Used at
// upload time so we can size cards to their actual aspect ratio rather than
// the one-size-fits-all defaults.

export async function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number } | null> {
  // SVGs don't have natural dimensions in the same way; skip and let the
  // caller fall back to a square default.
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') return null
  const url = URL.createObjectURL(file)
  try {
    return await new Promise<{ width: number; height: number } | null>((resolve) => {
      const img = new Image()
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
      img.onerror = () => resolve(null)
      img.src = url
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

// Same idea for video files — load metadata only (no full decode) and read the
// natural videoWidth / videoHeight. Resolves null if the browser can't decode
// the format (rare codec, corrupt file); callers fall back to the static
// default size.
export async function readVideoDimensions(
  file: File,
): Promise<{ width: number; height: number } | null> {
  if (!file.type.startsWith('video/')) return null
  const url = URL.createObjectURL(file)
  try {
    return await new Promise<{ width: number; height: number } | null>((resolve) => {
      const v = document.createElement('video')
      v.preload = 'metadata'
      v.muted = true
      v.onloadedmetadata = () => {
        const width = v.videoWidth
        const height = v.videoHeight
        if (width && height) resolve({ width, height })
        else resolve(null)
      }
      v.onerror = () => resolve(null)
      v.src = url
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

// Fit a media file's intrinsic dimensions to a target box, preserving aspect
// ratio. Lower bound prevents tiny thumbnails from feeling lost on the canvas;
// upper bound stops a 4K hero image / video from filling the viewport.
// Defaults tuned for moodboard cards (~280–400px feel right).
export function fitMediaSize(
  intrinsic: { width: number; height: number },
  options: { minSize?: number; maxSize?: number } = {},
): { w: number; h: number } {
  const { minSize = 200, maxSize = 400 } = options
  const aspect = intrinsic.width / intrinsic.height
  // Choose width by capping at maxSize, then derive height. If height overflows
  // maxSize (very tall portrait), cap on height instead.
  let w = Math.min(maxSize, Math.max(minSize, intrinsic.width))
  let h = Math.round(w / aspect)
  if (h > maxSize) {
    h = maxSize
    w = Math.round(h * aspect)
  }
  if (h < minSize) {
    h = minSize
    w = Math.round(h * aspect)
  }
  return { w, h }
}
